import { Flex, FormControl, FormLabel, Heading, Text } from "@chakra-ui/react";
import { InputControl, TextareaControl } from "formik-chakra-ui";
import React from "react";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import * as OrgService from "src/services/orgService";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form = () => {
  return (
    <>
      <Flex>
        <FormControl>
          <Flex direction="column">
            <FormLabel htmlFor="emailFooter">
              <Heading as="h3" variant="admin">
                Email footer
              </Heading>
              <Text color="blackAlpha.700" fontSize="sm">
                Every email sent from Avela will contain this footer
              </Text>
            </FormLabel>
            <TextareaControl
              textareaProps={{ placeholder: "Enter email footer" }}
              name="emailFooter"
            />
          </Flex>
        </FormControl>
      </Flex>
      <Flex direction="column">
        <FormControl>
          <Flex direction="column" gap="2">
            <Flex direction="column">
              <Heading as="h3" variant="admin">
                Email reply-to
              </Heading>
              <Text color="blackAlpha.700" fontSize="sm">
                Every email sent from Avela will contain this reply-to address
              </Text>
            </Flex>
            <InputControl
              label="Email address"
              name="emailReplyTo"
              inputProps={{
                maxW: "400px",
                placeholder: "E.g.: email@address.com",
              }}
            />
          </Flex>
        </FormControl>
      </Flex>
    </>
  );
};

export const FormikSchema = z.object({
  branding: z.any().transform((value): OrgConfig.BrandingConfig => value),
  emailFooter: z.string().min(1, { message: "This field is required" }),
  emailReplyTo: z.string().email("Invalid email address").or(z.literal("")),
});

type FormikType = z.infer<typeof FormikSchema>;

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.BrandingConfig>("Branding");

  const initialValues: RD.RemoteData<Error, FormikType> = React.useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (!config) {
        return {
          branding: OrgConfig.BrandingConfigSchema.default,
          emailFooter: OrgConfig.BrandingConfigSchema.default.message.footer,
          emailReplyTo:
            OrgConfig.BrandingConfigSchema.default.message.replyTo ?? "",
        };
      }

      return {
        branding: config,
        emailFooter: config.message.footer,
        emailReplyTo: config.message.replyTo ?? "",
      };
    });
  }, [remoteData]);

  const submit = React.useCallback(
    async (values: FormikType) => {
      await update({
        ...values.branding,
        message: {
          footer: values.emailFooter,
          replyTo: values.emailReplyTo,
        },
      } satisfies OrgConfig.BrandingConfig);
    },
    [update]
  );

  return { initialValues, submit };
}
